"use client"

import styled from "@emotion/styled"
import { APIProvider, AdvancedMarker, Map, Pin } from "@vis.gl/react-google-maps"
import { useLocale } from "next-intl"
import Image from "next/image"
import { useCallback, useState } from "react"
import { Map2Icon, Map3Icon, useIsDesktop } from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"
import { useLandingAnalytics } from "../analytics"

type Props = {
  data: any
  locale?: string
}

const APIKEY = "AIzaSyD2AanBqJJ8WM8JL6LJ3jjbYkUgD4Mk0Cc"
const MAPID = "40df340c1a7a4582"

// --- Styled Components ---

const Section = styled.section`
  background-color: var(--comp-bg, #ffffff);
  color: var(--comp-text, #020a0d);
  padding: 64px 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 120px 120px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 120px 240px;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
  }
`

const Headline = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: -0.8px;
  text-align: center;
  color: var(--comp-heading, #020a0d);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    line-height: 1.2;
    letter-spacing: 0;
  }
`

const MapControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
    align-items: flex-start;
  }
`

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 54px;
  border: 1px solid #878b9d;
  border-radius: 87px;
  padding: 8px;
  cursor: pointer;
  position: relative;
  background: #ffffff;
  gap: 4px;
`

const ToggleIcon = styled.button<{ active: boolean }>`
  width: 48px;
  height: 38px;
  border-radius: 87px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  background-color: ${({ active }) => (active ? "#020a0d" : "transparent")};
  color: ${({ active }) => (active ? "white" : "#020a0d")};
  padding: 0;
  font-size: 18px;

  &:focus-visible {
    outline: 2px dashed #020a0d;
    outline-offset: 2px;
  }
`

const MapContainer = styled.div<{ locale: string }>`
  position: relative;
  width: 100%;
  height: 266px;
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 567px;
  }

  .gmnoprint {
    z-index: 99999999;
    right: ${(p) => (p.locale === "he" ? "6px !important" : "unset !important")};
    left: ${(p) => (p.locale === "en" ? "6px !important" : "unset !important")};

    > div {
      border-radius: 8px !important;
    }
    img {
      width: 16px !important;
      height: 16px !important;
    }
    button {
      border-radius: 8px !important;
    }
  }
`

const FallbackMessage = styled.div`
  width: 100%;
  height: 266px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  color: #666;
  font-size: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 567px;
  }
`

// --- Main Component ---

function InteractiveMap({ data }: Props) {
  const locale = useLocale()
  const headline = extractProperty(data, "mapHeadline") as string | undefined
  const cmsLatitude = parseFloat(extractProperty(data, "mapLatitude") || "0")
  const cmsLongitude = parseFloat(extractProperty(data, "mapLongitude") || "0")
  const zoomLevel = parseInt(extractProperty(data, "mapZoomLevel") || "15", 10)
  const isDesktop = useIsDesktop()
  const desktopImageMedia = extractProperty(data, "mapImage", true) as any[] | undefined
  const mobileImageMedia = extractProperty(data, "mapImageMobile", true) as any[] | undefined
  const mapImageMedia = isDesktop
    ? desktopImageMedia
    : mobileImageMedia?.length
    ? mobileImageMedia
    : desktopImageMedia
  const mapImageUrl = mapImageMedia?.[0]?.url ? getUmbracoMediaUrl(mapImageMedia[0].url) : undefined
  const mapImageAlt = mapImageMedia?.[0]?.altText || ""
  const hiddenMapTab =
    extractProperty(data, "hiddenMapTab") === true ||
    extractProperty(data, "hiddenMapTab") === "true"
  const hiddenMapImageTab =
    extractProperty(data, "hiddenMapImageTab") === true ||
    extractProperty(data, "hiddenMapImageTab") === "true"
  const showMap = !hiddenMapTab
  const showImageOption = !!mapImageUrl && !hiddenMapImageTab
  const [showImage, setShowImage] = useState(!showMap && showImageOption)
  const analytics = useLandingAnalytics()
  const hasCoordinates = cmsLatitude !== 0 && cmsLongitude !== 0
  const position = { lat: cmsLatitude, lng: cmsLongitude }
  const showToggle = showMap && showImageOption

  /* click_text is the view being switched to, per the spec's
     "בהתאם למה שנבחר" (ASH-1854) */
  const toggleView = useCallback(() => {
    setShowImage((prev) => {
      const next = !prev
      analytics.mapToggle(next ? "image view" : "map view")
      return next
    })
  }, [analytics])

  if (!headline && !hasCoordinates) return null

  return (
    <Section>
      <ContentWrapper>
        {headline && <Headline>{headline}</Headline>}

        <MapControls>
          {showToggle && (
            <ToggleContainer
              role="group"
              aria-label={locale === "he" ? "החלף תצוגת מפה" : "Switch map view"}
            >
              <ToggleIcon
                active={!showImage}
                onClick={toggleView}
                aria-label={locale === "he" ? "תצוגת מפה" : "Map view"}
                aria-pressed={!showImage}
              >
                <Map3Icon color={!showImage ? "#fff" : "#020a0d"} />
              </ToggleIcon>
              <ToggleIcon
                active={showImage}
                onClick={toggleView}
                aria-label={locale === "he" ? "תצוגת תמונה" : "Image view"}
                aria-pressed={showImage}
              >
                <Map2Icon color={showImage ? "#fff" : "#020a0d"} />
              </ToggleIcon>
            </ToggleContainer>
          )}

          {(showMap && hasCoordinates) || showImageOption ? (
            <MapContainer locale={locale}>
              {showImage && showImageOption && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 5,
                  }}
                >
                  <Image src={mapImageUrl} fill alt={mapImageAlt} style={{ objectFit: "cover" }} />
                </div>
              )}
              {showMap && hasCoordinates && (
                <APIProvider apiKey={APIKEY} language={locale === "he" ? "iw" : "en"}>
                  <Map
                    defaultZoom={zoomLevel}
                    mapId={MAPID}
                    defaultCenter={position}
                    disableDefaultUI
                    zoomControl
                    zoomControlOptions={{ position: 6 }}
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <AdvancedMarker position={position}>
                      <Pin borderColor="green" />
                    </AdvancedMarker>
                  </Map>
                </APIProvider>
              )}
            </MapContainer>
          ) : !hiddenMapTab && !hiddenMapImageTab ? (
            <FallbackMessage>
              {locale === "he" ? "לא ניתן לטעון את המפה" : "Unable to load map"}
            </FallbackMessage>
          ) : null}
        </MapControls>
      </ContentWrapper>
    </Section>
  )
}

export default InteractiveMap
